import { PRO_VERIFICATION_PENDING_LABEL } from "../../lib/verification-copy";
import { Fragment, useState, type ReactElement, type ReactNode } from "react";

export type OfferCardDeliveryState = "none" | "pending_labeled" | "verified";

/**
 * Presentation-only relationship chrome. This is deliberately separate from
 * an offer's API data: the consumer already knows whether a displayed agent
 * is theirs, and the shared card stays the single render path for every
 * offer surface.
 */
export type OfferCardChrome = "own-agent";

export interface OfferCardIncentiveItem {
  type: string;
  value?: number;
}

export interface OfferCardItemLanes {
  rebate?: OfferCardIncentiveItem[];
  cash?: OfferCardIncentiveItem[];
  perks?: OfferCardIncentiveItem[];
}

/** Everything a card renders except the dollar-back estimate. */
export interface OfferCardBaseData {
  proDisplayName?: string;
  proPhotoUrl?: string;
  proBrokerage?: string;
  deliveryState?: OfferCardDeliveryState;
  commissionPct?: number | null;
  items: OfferCardItemLanes;
  note?: string;
  expiresAt?: string | null;
  standing?: boolean;
  locked?: boolean;
  lockedCopy?: string;
  actionLabel?: string;
  contextLabel?: string;
  /** Adds the M8-D linked-agent chrome without forking the shared card. */
  chrome?: OfferCardChrome;
  /** Buyer tier the offer applies at (e.g. "t2") — rendered as a chip. */
  buyerTier?: string | null;
}

/** The server-computed estimate a buyer sees (inbox / detail DTOs). */
export interface OfferCardEstimatedSavings {
  cents: number;
  basisCents: number;
  basis: "target_price" | "buying_power";
}

/**
 * A range estimate from an authorized economic basis. The Pro composer
 * currently omits price-based estimates because passport bands are display-only.
 */
export interface OfferCardEstimatedSavingsRange {
  lowCents: number;
  highCents: number;
}

/**
 * Exact and range estimates are mutually exclusive at
 * the type level — a caller can never pass both.
 */
export type OfferCardSavings =
  | {
      estimatedSavings?: OfferCardEstimatedSavings | null;
      estimatedSavingsRange?: never;
    }
  | {
      estimatedSavings?: never;
      estimatedSavingsRange?: OfferCardEstimatedSavingsRange | null;
    };

export type OfferCardData = OfferCardBaseData & OfferCardSavings;

const ITEM_LABELS: Record<string, string> = {
  commission_rebate: "Commission rebate",
  closing_cost_credit: "Closing cost credit",
  home_warranty: "Home warranty",
  inspection_credit: "Inspection credit",
  buyer_strategy_session: "Buyer strategy session",
  custom_market_report: "Custom neighborhood and market report",
  priority_touring: "Priority / extended-hours touring",
  flexible_buyer_agreement: "Flexible buyer-agreement terms",
  response_time_sla: "Response-time SLA",
};

/**
 * A short note under an item's row ONLY where it carries a real disclosure.
 * Labels are self-explanatory everywhere else, so the detail card keeps its
 * itemised rows to label + value.
 */
const ITEM_DESCRIPTIONS: Record<string, string> = {
  inspection_credit: "Delivered outside the closing disclosure.",
  closing_cost_credit: "Credited at closing, on top of your rebate.",
};

export function itemLabel(type: string): string {
  return ITEM_LABELS[type] ?? type;
}

export function itemDescription(type: string): string | undefined {
  return ITEM_DESCRIPTIONS[type];
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  return `${Number.parseFloat(value.toFixed(2)).toString()}%`;
}

export function formatDollars(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Compact whole-dollar figure for a home price ("$680k", "$1.3M") — the
 * same shape the buyer passport uses for its money bands.
 */
export function formatDollarsCompact(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  const thousands = Math.round(value / 1_000);
  if (Math.abs(thousands) >= 1_000) {
    return `$${Number.parseFloat((value / 1_000_000).toFixed(1)).toString()}M`;
  }
  if (Math.abs(value) >= 1_000) return `$${thousands}k`;
  return formatDollars(value);
}

/**
 * One source of truth for an offer's headline terms: the first valued rebate,
 * the cash-lane total, and the "X% rebate + $Y" line every summary row
 * renders. termsLine is null when the offer carries no valued rebate.
 */
export function offerTermsSummary(items: OfferCardItemLanes): {
  rebatePct: number | null;
  cashTotal: number;
  termsLine: string | null;
} {
  const rebate = items.rebate?.find((item) => item.value != null);
  const cashTotal = (items.cash ?? []).reduce(
    (total, item) => total + (item.value ?? 0),
    0,
  );
  return {
    rebatePct: rebate?.value ?? null,
    cashTotal,
    termsLine:
      rebate?.value == null
        ? null
        : `${formatPercent(rebate.value)} rebate + ${formatDollars(cashTotal)}`,
  };
}

/**
 * The compact key-facts line every summary row renders (offers-inbox strip,
 * profile ladder): commission, rebate, and cash incentives — each ONLY when
 * the offer actually carries it, so a bare-commission standing offer reads
 * "2.5% commission" instead of a row of dashes. Empty when nothing applies;
 * callers omit the line rather than render an empty one.
 */
export function offerKeyFacts(
  data: Pick<OfferCardData, "commissionPct" | "items">,
): string[] {
  const { rebatePct, cashTotal } = offerTermsSummary(data.items);
  const facts: string[] = [];
  if (data.commissionPct != null)
    facts.push(`${formatPercent(data.commissionPct)} commission`);
  if (rebatePct != null) facts.push(`${formatPercent(rebatePct)} rebate`);
  if (cashTotal > 0) facts.push(`${formatDollars(cashTotal)} cash incentives`);
  return facts;
}

/**
 * The offer in one sentence — the card headline ("0.5% back at closing").
 * Reads the same terms `offerTermsSummary` resolves; the strongest term the
 * offer carries leads (rebate, then cash, then commitments, then the bare
 * commission).
 */
export function offerHeadline(
  data: Pick<OfferCardData, "commissionPct" | "items" | "standing">,
): string {
  const { rebatePct, cashTotal } = offerTermsSummary(data.items);
  if (rebatePct != null) return `${formatPercent(rebatePct)} back at closing`;
  if (cashTotal > 0) return `${formatDollars(cashTotal)} in cash incentives`;
  const perks = data.items.perks?.length ?? 0;
  if (perks > 0)
    return `${perks} service ${perks === 1 ? "commitment" : "commitments"} included`;
  if (data.commissionPct != null)
    return `${formatPercent(data.commissionPct)} commission`;
  return data.standing ? "Standing offer" : "Marketplace offer";
}

/** What the exact estimate's basis figure is, in buyer words. */
const SAVINGS_BASIS_NOUN: Partial<Record<string, string>> = {
  target_price: "home",
  buying_power: "budget",
};

/**
 * The dollar-back line under the headline. Exact: "About $3,400 back on a
 * $680k home" (server-computed cents, rounded to whole dollars); a
 * buying-power basis is the buyer's budget, not a home price, so it reads
 * "on a $680k budget", and an unknown basis drops the clause rather than
 * guess. Range: "≈ $2,500–$4,100 back". Null — never "$0" — when there is
 * no estimate or it rounds to nothing.
 */
export function offerSavingsLine(data: OfferCardSavings): string | null {
  const exact = data.estimatedSavings;
  if (exact) {
    const dollars = Math.round(exact.cents / 100);
    if (dollars <= 0) return null;
    const basisDollars = Math.round(exact.basisCents / 100);
    const noun = SAVINGS_BASIS_NOUN[exact.basis];
    return noun && basisDollars > 0
      ? `About ${formatDollars(dollars)} back on a ${formatDollarsCompact(basisDollars)} ${noun}`
      : `About ${formatDollars(dollars)} back`;
  }
  const range = data.estimatedSavingsRange;
  if (range) {
    const low = Math.max(0, Math.round(Math.min(range.lowCents, range.highCents) / 100));
    const high = Math.round(Math.max(range.lowCents, range.highCents) / 100);
    if (high <= 0) return null;
    return low === high
      ? `≈ ${formatDollars(high)} back`
      : `≈ ${formatDollars(low)}–${formatDollars(high)} back`;
  }
  return null;
}

/**
 * The dollar figure alone — "About $3,400 back" (exact) or "≈ $2,500–$4,100
 * back" (range) — with no basis clause. This is what LEADS a card's headline
 * whenever a server-computed estimate exists (task 2140): the figure is the
 * card's one emphasis, and everything that composes it (terms, basis,
 * commission) moves to the subline. Null — never "$0" — for no estimate or
 * one that rounds to nothing, exactly like `offerSavingsLine`.
 */
function estimateHeadline(
  data: OfferCardSavings,
): { headline: string; kind: "exact" | "range" } | null {
  const exact = data.estimatedSavings;
  if (exact) {
    const dollars = Math.round(exact.cents / 100);
    if (dollars > 0) return { headline: `About ${formatDollars(dollars)} back`, kind: "exact" };
  }
  const range = data.estimatedSavingsRange;
  if (range) {
    const low = Math.max(0, Math.round(Math.min(range.lowCents, range.highCents) / 100));
    const high = Math.round(Math.max(range.lowCents, range.highCents) / 100);
    if (high > 0) {
      return {
        headline:
          low === high
            ? `≈ ${formatDollars(high)} back`
            : `≈ ${formatDollars(low)}–${formatDollars(high)} back`,
        kind: "range",
      };
    }
  }
  return null;
}

/** "on a $680k home" / "on a $680k budget" — the exact estimate's basis
 * clause alone, dropped (null) for an unknown basis or a non-positive
 * figure. Pro's range headline never calls this — a rebate % beside a home
 * price would let an agent work back to the buyer's exact target price. */
function basisClause(exact: OfferCardEstimatedSavings): string | null {
  const basisDollars = Math.round(exact.basisCents / 100);
  const noun = SAVINGS_BASIS_NOUN[exact.basis];
  if (!noun || basisDollars <= 0) return null;
  return `on a ${formatDollarsCompact(basisDollars)} ${noun}`;
}

function lowerFirst(label: string): string {
  return label.length === 0
    ? label
    : label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * The terms behind a dollar-lead headline: the rebate % first, then the cash
 * items — "1% rebate + $450 inspection credit". More than two cash items
 * collapse into their total ("+ $1,500 in credits") rather than listing
 * every row. Null when the offer carries neither (never reached in practice,
 * since those are exactly what a positive estimate is computed from).
 */
function offerTermsPhrase(items: OfferCardItemLanes): string | null {
  const { rebatePct, cashTotal } = offerTermsSummary(items);
  const parts: string[] = [];
  if (rebatePct != null) parts.push(`${formatPercent(rebatePct)} rebate`);

  const cashItems = (items.cash ?? []).filter((item) => item.value != null);
  if (cashItems.length > 2) {
    parts.push(`${formatDollars(cashTotal)} in credits`);
  } else {
    for (const item of cashItems) {
      parts.push(
        `${formatDollars(item.value as number)} ${lowerFirst(itemLabel(item.type))}`,
      );
    }
  }
  return parts.length > 0 ? parts.join(" + ") : null;
}

/**
 * Items delivered AT the closing disclosure vs OFF it (backend catalog.go
 * `DeliveryMoment`: "at_closing" / "off_cd"). The lender-approval /
 * closing-cost-cap language only applies to at-closing items — stating it
 * for an off-closing-only offer (e.g. a cash-only home-warranty +
 * inspection-credit offer) is a false claim about those credits (task 2142).
 */
const AT_CLOSING_ITEM_TYPES: ReadonlySet<string> = new Set([
  "commission_rebate",
  "closing_cost_credit",
]);

const OFF_CLOSING_ITEM_TYPES: ReadonlySet<string> = new Set([
  "home_warranty",
  "inspection_credit",
]);

function hasAtClosingValue(items: OfferCardItemLanes): boolean {
  const all = [...(items.rebate ?? []), ...(items.cash ?? [])];
  return all.some(
    (item) => AT_CLOSING_ITEM_TYPES.has(item.type) && item.value != null,
  );
}

/** Compact one-line disclosure shown wherever a card leads with a dollar
 * estimate. The detail card keeps its own longer two-variant footnote
 * instead (task 2139) — this one is for every other card. The "exact" kind
 * forks on delivery timing (task 2142): a range estimate never states a
 * dollar figure at all, so its wording never changes. */
const CONDITIONS_NOTE = {
  exactAtClosing:
    "Estimate. Credits at closing need lender approval and can't exceed your closing costs.",
  exactOffClosing:
    "Estimate. These credits are paid outside closing, so lender limits don't apply.",
  range: "Estimate across this buyer's price bracket.",
} as const;

function ConditionsFootnote({
  kind,
  items,
}: {
  kind: "exact" | "range";
  items: OfferCardItemLanes;
}): ReactElement {
  const text =
    kind === "range"
      ? CONDITIONS_NOTE.range
      : hasAtClosingValue(items)
        ? CONDITIONS_NOTE.exactAtClosing
        : CONDITIONS_NOTE.exactOffClosing;
  return (
    <p
      className="text-xs leading-5 text-muted-foreground"
      data-testid="offer-card-conditions-note"
    >
      {text}
    </p>
  );
}

export function commissionFramingLine(
  data: Pick<OfferCardData, "commissionPct" | "items">,
): string | null {
  if (data.commissionPct == null) return null;
  const commission = `${formatPercent(data.commissionPct)} commission`;
  const rebate = data.items.rebate?.find((item) => item.value != null);
  if (!rebate || rebate.value == null) return commission;
  return `${commission} · offering ${formatPercent(rebate.value)} rebate`;
}

export function formatExpiryShort(
  expiresAt: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!expiresAt) return null;
  const target = new Date(expiresAt);
  if (Number.isNaN(target.getTime())) return null;
  const days = Math.ceil((target.getTime() - now.getTime()) / 86_400_000);
  if (days <= 0) return "Expires today";
  if (days === 1) return "1 day left";
  return `${days} days left`;
}

function initials(name?: string): string {
  return (name ?? "Agent")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

/** 44px identity tile; failed or missing photos fall back to initials. */
function AgentTile({
  name,
  photoUrl,
}: {
  name?: string;
  photoUrl?: string;
}): ReactElement {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (photoUrl && photoUrl !== failedUrl) {
    // Preserve other query fields and fragments; replace thumbnail controls.
    const [url = "", fragment] = photoUrl.split("#", 2);
    const queryAt = url.indexOf("?");
    const path = queryAt < 0 ? url : url.slice(0, queryAt);
    const params = new URLSearchParams(queryAt < 0 ? "" : url.slice(queryAt + 1));
    params.set("width", "88");
    params.set("height", "88");
    params.set("fit", "crop");
    params.set("quality", "85");
    const src = `${path}?${params}${fragment === undefined ? "" : `#${fragment}`}`;
    return (
      <img
        alt=""
        className="size-11 shrink-0 rounded-xl object-cover"
        onError={() => setFailedUrl(photoUrl)}
        src={src}
      />
    );
  }
  return (
    <span
      aria-hidden
      className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground"
    >
      {initials(name)}
    </span>
  );
}

/**
 * The muted identity line: "Brokerage · Verified agent". "Verified" is only
 * ever stated for a verified delivery state; a pending agent reads
 * "License verification in progress" in the warning tone instead.
 *
 * The status is the card's trust signal, so it never ellipsizes: it sits in a
 * `shrink-0` span and only the brokerage name truncates when a phone-width
 * header runs out of room.
 *
 * Below `sm` the text column beside the avatar and expiry chip is ~158px, too
 * narrow to share with the status, so the meta stacks: the brokerage gets its
 * own full-width line and the status sits under it with no separator. From
 * `sm` it is the designed single line.
 */
function AgentMeta({
  data,
  fallback,
}: {
  data: OfferCardData;
  fallback: string;
}): ReactElement {
  let status: ReactNode = null;
  if (data.deliveryState === "verified") status = <span>Verified agent</span>;
  if (data.deliveryState === "pending_labeled")
    status = (
      <span className="font-semibold text-warning">
        {PRO_VERIFICATION_PENDING_LABEL}
      </span>
    );
  const lead = data.proBrokerage ?? (status ? null : fallback);

  return (
    <p
      className="flex min-w-0 flex-col text-[13px] text-muted-foreground sm:flex-row sm:items-baseline"
      data-testid="offer-agent-meta"
    >
      {lead && <span className="min-w-0 truncate">{lead}</span>}
      {status && (
        <span className="shrink-0 whitespace-nowrap">
          {/* whitespace-pre keeps the leading space, which a flex item
              would otherwise collapse at its line start. Hidden below `sm`,
              where the status stacks under the brokerage. */}
          {lead && (
            <span aria-hidden className="hidden whitespace-pre sm:inline">
              {" · "}
            </span>
          )}
          {status}
        </span>
      )}
    </p>
  );
}

/**
 * The headline sentence and, when there is anything to say, the sub-line.
 *
 * Task 2140: when the offer carries a server-computed dollar estimate
 * (exact) or a bracket range (Pro preview), the DOLLAR FIGURE leads the
 * headline — it is the card's one emphasis — and everything that composes
 * it (terms, basis, commission) moves to the sub-line. With no usable
 * estimate the headline falls back to today's `offerHeadline` sentence,
 * unchanged.
 */
function OfferHeadline({
  data,
  size = "lg",
}: {
  data: OfferCardData;
  size?: "lg" | "md";
}): ReactElement {
  const estimate = estimateHeadline(data);
  const commission =
    data.commissionPct == null
      ? null
      : `${formatPercent(data.commissionPct)} commission`;

  let headlineText: string;
  let headlineAriaLabel: string | null = null;
  let leadText: string | null = null;

  if (estimate) {
    headlineText = estimate.headline;
    const terms = offerTermsPhrase(data.items);
    // Pro's range headline never gets a basis clause — a rebate % beside a
    // home price would let an agent work back to the buyer's exact target
    // price, so only the exact (buyer-surface) estimate ever carries it.
    const basis =
      estimate.kind === "exact" && data.estimatedSavings
        ? basisClause(data.estimatedSavings)
        : null;
    const leadParts = [terms, basis].filter(
      (part): part is string => part != null,
    );
    leadText = leadParts.length > 0 ? leadParts.join(" · ") : null;
    // The basis clause moves to the sub-line visually, but assistive tech
    // should still hear the figure and its basis as one natural sentence.
    headlineAriaLabel = basis ? `${headlineText} ${basis}` : null;
  } else {
    headlineText = offerHeadline(data);
  }

  const hasSubline = leadText != null || commission != null;

  return (
    <div>
      <p
        className={`font-bold leading-[1.1] tracking-[-0.015em] text-foreground ${
          // 24px below `sm` so a cash headline fits one line on a phone.
          size === "lg" ? "text-[24px] sm:text-[30px]" : "text-xl"
        }`}
        data-testid="offer-card-headline"
        {...(headlineAriaLabel ? { "aria-label": headlineAriaLabel } : {})}
      >
        {headlineText}
      </p>
      {hasSubline && (
        <p
          className="mt-1.5 text-[13.5px] leading-5 text-muted-foreground"
          data-testid="offer-card-subline"
        >
          {leadText && <span data-testid="offer-card-terms">{leadText}</span>}
          {leadText && commission && " "}
          {/* The dot rides with the commission so a wrap at phone width
              moves "· N% commission" to line 2 instead of stranding it. */}
          {commission && (
            <span
              className="whitespace-nowrap"
              data-testid="offer-card-commission"
            >
              {leadText && <span aria-hidden>· </span>}
              {commission}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function ExpiryChip({ expiry }: { expiry: string }): ReactElement {
  return (
    <span
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning-foreground"
      data-testid="offer-card-expiry"
    >
      {expiry}
    </span>
  );
}

/** Label/value rows divided by soft borders: the offer's itemised terms. */
function offerTermRows(
  items: OfferCardItemLanes,
): { key: string; label: string; value: string }[] {
  const rows: { key: string; label: string; value: string }[] = [];
  const { rebatePct } = offerTermsSummary(items);
  if (rebatePct != null)
    rows.push({
      key: "rebate",
      label: "Rebate to you",
      value: formatPercent(rebatePct),
    });
  (items.cash ?? []).forEach((item, index) =>
    rows.push({
      key: `cash-${item.type}-${index}`,
      label: itemLabel(item.type),
      value: item.value == null ? "Included" : formatDollars(item.value),
    }),
  );
  (items.perks ?? []).forEach((item, index) =>
    rows.push({
      key: `perk-${item.type}-${index}`,
      label: itemLabel(item.type),
      value: "Included",
    }),
  );
  return rows;
}

function OfferTerms({ items }: { items: OfferCardItemLanes }): ReactElement | null {
  const rows = offerTermRows(items);
  if (rows.length === 0) return null;
  return (
    <dl
      aria-label="Offer terms"
      className="divide-y divide-border-soft"
      data-testid="offer-card-included-items"
    >
      {rows.map((row) => (
        <div
          className="flex items-baseline justify-between gap-3 py-2.5 text-sm"
          data-testid="offer-card-term"
          key={row.key}
        >
          <dt className="min-w-0 text-muted-foreground">{row.label}</dt>
          <dd className="shrink-0 font-semibold tabular-nums text-foreground">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Quiet, display-only summary for a standing offer in an inbox lane. The
 * consumer surface owns the Link/button wrapper so this primitive stays
 * reusable without coupling routing into shared.
 */
export function StandingOfferRow({
  data,
  topRightInset = false,
}: {
  data: OfferCardData;
  /**
   * Reserve the row's top-right corner for a control the OWNING surface
   * overlays (e.g. the offers-inbox dismiss ×). The control cannot live
   * inside this row because the row is usually wrapped in a link, and a
   * button nested in an anchor is invalid HTML — so the row only leaves
   * room, and the owner positions the control absolutely over it.
   */
  topRightInset?: boolean;
}): ReactElement {
  const facts = offerKeyFacts(data);
  const estimate = estimateHeadline(data);
  const commitments = (data.items.perks ?? []).map((item) =>
    itemLabel(item.type),
  );

  // The headline-sized slot ("standing-offer-facts") shows the key-facts
  // ladder sentence by default. When the offer carries a dollar estimate,
  // that figure LEADS instead (task 2140) and the ladder sentence moves
  // down into a muted sub-line ("standing-offer-terms") — the same
  // dollar-lead rule OfferCard follows, at this row's smaller headline size.
  function factsLine(
    className: string,
    testId: string,
  ): ReactElement | null {
    if (facts.length === 0) return null;
    return (
      <p className={className} data-testid={testId}>
        {facts.map((fact, index) => (
          <Fragment key={fact}>
            {index > 0 && " "}
            <span className="whitespace-nowrap">
              {index > 0 && "· "}
              {fact}
            </span>
          </Fragment>
        ))}
      </p>
    );
  }

  // One deliberate step down from OfferCard's 24/30px headline, same weight
  // and tracking. It wraps rather than ellipsizing a money fact; each fact
  // stays whole and the dot rides with the next one.
  const headlineFactsClassName =
    "mt-3 text-[20px] font-bold leading-[1.15] tabular-nums tracking-[-0.015em] text-foreground sm:text-[22px]";

  // Fixed slot order — header → headline → (terms sub-line / conditions
  // note when a dollar figure leads) → footer — and the card fills its grid
  // cell (`h-full`) with the footer pinned to the bottom (`mt-auto`), so two
  // cards side by side are equal height with their "View offer" affordances
  // on one line even when only one carries an estimate. The owning surface
  // must let the cell's height reach the card (its wrappers need `h-full`
  // too).
  //
  // Attached tier (styling-system.md "Depth tiers"): the app's one card
  // fill, flat — no shadow — so it reads one step lighter than the Detached
  // `OfferCard` a buyer was sent, alongside its smaller radius and headline.
  return (
    <article
      className="flex h-full flex-col rounded-2xl border border-transparent bg-popover px-4 py-3.5 transition-[background-color,border-color] duration-200 ease-brand group-hover:border-primary/30 group-hover:bg-primary-soft/45 group-focus-visible:border-primary/45 group-focus-visible:bg-primary-soft/45 motion-reduce:transition-none"
      data-testid="standing-offer-row"
    >
      <div className="flex items-start gap-3">
        <AgentTile name={data.proDisplayName} photoUrl={data.proPhotoUrl} />
        <div className="min-w-0 flex-1 pt-0.5">
          <div
            className={`flex items-center gap-2 ${topRightInset ? "pr-8" : ""}`}
          >
            <p className="min-w-0 flex-1 truncate text-sm font-bold text-foreground">
              {data.proDisplayName ?? "Agent"}
            </p>
            {data.buyerTier && (
              <span className="flex min-w-9 shrink-0 items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
                {data.buyerTier.toUpperCase()}
              </span>
            )}
          </div>
          <AgentMeta data={data} fallback="Standing offer" />
        </div>
      </div>
      {estimate ? (
        <>
          <p className={headlineFactsClassName} data-testid="standing-offer-facts">
            {estimate.headline}
          </p>
          {factsLine(
            "mt-1 text-[13.5px] leading-5 text-muted-foreground",
            "standing-offer-terms",
          )}
          <ConditionsFootnote items={data.items} kind={estimate.kind} />
        </>
      ) : (
        factsLine(headlineFactsClassName, "standing-offer-facts")
      )}
      <div
        className="mt-auto flex items-end justify-between gap-3 pt-3"
        data-testid="standing-offer-footer"
      >
        <p className="min-w-0 truncate text-xs text-muted-foreground">
          {commitments.length > 0
            ? commitments.join(" · ")
            : "No service commitments"}
        </p>
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-primary">
          View offer
          <span aria-hidden>›</span>
        </span>
      </div>
    </article>
  );
}

export function OfferCard({
  data,
  footerActions,
}: {
  data: OfferCardData;
  /** Optional interactive actions supplied by the owning surface. */
  footerActions?: ReactNode;
}): ReactElement {
  if (data.locked) {
    return (
      <div
        className="rounded-3xl bg-popover p-5 opacity-70 shadow-sm"
        data-testid="offer-card-locked"
      >
        <p className="text-base font-semibold text-foreground">
          {data.proDisplayName ?? "Agent"}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          {data.lockedCopy ?? "Unlock by verifying your finances"}
        </p>
      </div>
    );
  }

  const expiry = formatExpiryShort(data.expiresAt);
  const ownAgent = data.chrome === "own-agent";
  const estimate = estimateHeadline(data);

  return (
    <article
      // Detached tier (styling-system.md): a tangible offer from another
      // party floats on `shadow-sm` with a flat `bg-popover` fill and NO
      // separation border. The own-agent 1.5px info border is a semantic
      // marker ("from your agent"), not an edge, so it stays.
      className={`relative flex flex-col gap-4.5 rounded-3xl bg-popover p-5 shadow-sm sm:p-6 ${
        ownAgent ? "border-[1.5px] border-info" : ""
      }`}
      data-offer-card-chrome={ownAgent ? "own-agent" : "default"}
      data-testid="offer-card"
    >
      <div className="flex items-start gap-3">
        <AgentTile name={data.proDisplayName} photoUrl={data.proPhotoUrl} />
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="truncate text-base font-bold text-foreground">
            {data.proDisplayName ?? "Agent"}
          </p>
          <AgentMeta
            data={data}
            fallback={data.standing ? "Standing offer" : "Marketplace agent"}
          />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {ownAgent && <OwnAgentBadge testId="offer-card-own-agent-badge" />}
          {expiry ? (
            <ExpiryChip expiry={expiry} />
          ) : (
            <span className="inline-flex items-center whitespace-nowrap rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground">
              {data.standing ? "Standing offer" : "Active offer"}
            </span>
          )}
        </div>
      </div>

      <OfferHeadline data={data} />

      <OfferTerms items={data.items} />

      {estimate && <ConditionsFootnote items={data.items} kind={estimate.kind} />}

      <div className="flex min-h-10 items-center justify-end gap-3">
        {footerActions ?? (
          <span className="inline-flex items-center gap-2 text-sm font-bold text-primary">
            {data.actionLabel ??
              (data.standing ? "View profile" : "View offer")}
            <span aria-hidden>›</span>
          </span>
        )}
      </div>
    </article>
  );
}

const CASH_TYPES = [
  "home_warranty",
  "inspection_credit",
  "closing_cost_credit",
] as const;

/**
 * Retired as selectable items (SILL-525): the SILL-511 evaluation folded
 * closing-cost credit into the commission rebate (the rebate IS delivered as
 * a closing-statement credit — see the delivery disclosure footnote). A
 * retired type renders only when a grandfathered offer actually carries it —
 * never in the "Not included" line.
 */
const RETIRED_CASH_TYPES: ReadonlySet<string> = new Set([
  "closing_cost_credit",
]);

const SERVICE_TYPES = [
  "flexible_buyer_agreement",
  "buyer_strategy_session",
  "custom_market_report",
  "priority_touring",
  "response_time_sla",
] as const;

/** Catalog order first; types the catalog doesn't know keep arrival order. */
function sortByCatalog(
  items: OfferCardIncentiveItem[],
  catalog: readonly string[],
): OfferCardIncentiveItem[] {
  const rank = (type: string) => {
    const index = catalog.indexOf(type);
    return index === -1 ? catalog.length : index;
  };
  return [...items].sort((a, b) => rank(a.type) - rank(b.type));
}

/**
 * One of the detail card's equal-size summary tiles. All three share the
 * same neutral inset fill (task 2140) — the dollar headline above is now
 * the card's one emphasis, so a tile never competes with it. Below `sm`
 * each tile is a compact full-width row (label left, value right) — 3-up
 * at phone width wrapped every label; from `sm` the label sits above the value.
 */
function SummaryTile({
  label,
  value,
}: {
  label: string;
  /** Null renders "None" in the muted tone — a tile never invents a figure. */
  value: string | null;
}): ReactElement {
  return (
    <div
      className="flex min-w-0 flex-row items-center justify-between gap-3 rounded-2xl bg-muted/55 px-4 py-2.5 sm:flex-col sm:items-start sm:justify-start sm:gap-1 sm:py-3"
      data-testid="detailed-offer-summary-tile"
    >
      <dt className="min-w-0 text-xs leading-4 text-muted-foreground">
        {label}
      </dt>
      <dd
        className={`shrink-0 text-base font-bold leading-tight tabular-nums tracking-[-0.01em] sm:text-xl ${
          value == null ? "text-muted-foreground" : "text-foreground"
        }`}
      >
        {value ?? "None"}
      </dd>
    </div>
  );
}

/**
 * An itemised term: label on the left, value on the right. `note` is only
 * ever a short disclosure (see ITEM_DESCRIPTIONS); `quiet` mutes a derived
 * figure so it never competes with the headline terms.
 */
function DetailTermRow({
  label,
  note,
  quiet = false,
  value,
}: {
  label: string;
  note?: string;
  quiet?: boolean;
  value: string;
}): ReactElement {
  return (
    <div
      className="flex items-baseline justify-between gap-4 py-2.5 text-sm"
      data-testid="detailed-offer-term"
    >
      <dt
        className={`min-w-0 ${quiet ? "text-muted-foreground" : "text-foreground"}`}
      >
        {label}
        {note && (
          <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
            {note}
          </span>
        )}
      </dt>
      <dd
        className={`shrink-0 tabular-nums ${
          quiet ? "text-muted-foreground" : "font-semibold text-foreground"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * A sentence-case section: its present terms as rows, then everything the
 * offer leaves out collapsed into ONE muted line (omitted when empty).
 */
function DetailSection({
  children,
  notIncluded = [],
  title,
}: {
  children?: ReactNode;
  notIncluded?: string[];
  title: string;
}): ReactElement {
  return (
    <section aria-label={title} className="mt-6">
      <h4 className="text-base font-semibold text-foreground">{title}</h4>
      {children && (
        <dl className="mt-1 divide-y divide-border-soft">{children}</dl>
      )}
      {notIncluded.length > 0 && (
        <p
          className={`text-xs leading-5 text-muted-foreground ${
            children ? "mt-1.5" : "mt-1"
          }`}
          data-testid="detailed-offer-not-included"
        >
          Not included: {notIncluded.join(", ")}
        </p>
      )}
    </section>
  );
}

/**
 * Canonical full-fidelity marketplace offer treatment. It is deliberately
 * display-only: consumer accept/request chrome remains a sibling so standing
 * offers and active offers keep their distinct server actions.
 *
 * Anatomy: header → headline + savings line → note → three equal summary
 * tiles (rebate, cash total, commitments) → itemised sections of plain
 * label/value rows, each closing with one collapsed "Not included" line →
 * the rebate delivery disclaimer as a footnote. Every figure is read from
 * the offer's own items; an absent term reads "None", never "$0".
 */
export function DetailedOfferCard({
  data,
}: {
  data: OfferCardData;
}): ReactElement {
  const rebate = data.items.rebate?.find(
    (item) => item.type === "commission_rebate" && item.value != null,
  );
  const cash = sortByCatalog(data.items.cash ?? [], CASH_TYPES);
  const perks = sortByCatalog(data.items.perks ?? [], SERVICE_TYPES);
  const cashTypes = new Set(cash.map((item) => item.type));
  const perkTypes = new Set(perks.map((item) => item.type));
  const cashTotal = cash.reduce((total, item) => total + (item.value ?? 0), 0);
  // Net commission is only worth stating when a rebate changes it.
  const netCommission =
    data.commissionPct != null && rebate?.value != null
      ? Math.max(0, data.commissionPct - rebate.value)
      : null;
  const cashNotIncluded = CASH_TYPES.filter(
    (type) => !RETIRED_CASH_TYPES.has(type) && !cashTypes.has(type),
  ).map(itemLabel);
  const servicesNotIncluded = SERVICE_TYPES.filter(
    (type) => !perkTypes.has(type),
  ).map(itemLabel);
  // task 2142: when the offer carries no at-closing item at all (no rebate,
  // no grandfathered closing_cost_credit), the rebate-delivery sentence
  // above is never shown — it would be false for credits nobody delivers at
  // closing. An off-closing-only offer (e.g. home warranty + inspection
  // credit) gets its own short sentence instead, and only when the card
  // actually leads with an estimate; with no estimate there is nothing to
  // caveat.
  const estimate = estimateHeadline(data);
  const hasOffClosingOnly =
    rebate == null &&
    !cashTypes.has("closing_cost_credit") &&
    cash.some(
      (item) => OFF_CLOSING_ITEM_TYPES.has(item.type) && item.value != null,
    );
  let cashTile: string | null = null;
  if (cashTotal > 0) cashTile = formatDollars(cashTotal);
  else if (cash.length > 0) cashTile = "Included";
  const expiry = formatExpiryShort(data.expiresAt);
  const ownAgent = data.chrome === "own-agent";

  return (
    <article
      className={`relative rounded-3xl bg-popover p-5 shadow-sm sm:p-7 ${
        ownAgent ? "border-[1.5px] border-info" : ""
      }`}
      data-offer-card-chrome={ownAgent ? "own-agent" : "default"}
      data-testid="detailed-offer-card"
    >
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <AgentTile name={data.proDisplayName} photoUrl={data.proPhotoUrl} />
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-foreground">
              {data.proDisplayName ?? "Agent"}
            </h3>
            <AgentMeta data={data} fallback="Marketplace agent" />
          </div>
        </div>
        <div className="flex flex-col items-start gap-1.5 sm:items-end">
          <div
            className="flex flex-nowrap items-center gap-2 sm:justify-end"
            data-testid="detailed-offer-card-badges"
          >
            <span className="inline-flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-primary-soft px-2.5 py-1 text-xs font-semibold text-primary">
              <span className="size-1.5 rounded-full bg-primary" />
              {data.contextLabel ??
                (data.standing ? "Available to you" : "Made for you")}
            </span>
            {ownAgent && (
              <OwnAgentBadge testId="detailed-offer-card-own-agent-badge" />
            )}
          </div>
          {expiry && <ExpiryChip expiry={expiry} />}
        </div>
      </header>

      <div className="mt-6">
        <OfferHeadline data={data} />
      </div>

      {data.note && (
        <blockquote className="mt-5 rounded-2xl bg-muted/55 px-5 py-4 text-[15px] italic leading-7 text-foreground">
          “{data.note}”
        </blockquote>
      )}

      <dl
        aria-label="Offer summary"
        className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3"
        data-testid="detailed-offer-summary"
      >
        <SummaryTile
          label="Rebate to you"
          value={rebate?.value == null ? null : formatPercent(rebate.value)}
        />
        <SummaryTile label="Cash incentives" value={cashTile} />
        <SummaryTile
          label="Commitments"
          value={perks.length > 0 ? `${perks.length} included` : null}
        />
      </dl>

      {data.commissionPct != null && (
        <DetailSection title="Commission">
          <DetailTermRow
            label="Agent commission"
            value={formatPercent(data.commissionPct)}
          />
          {netCommission != null && (
            <DetailTermRow
              label="Net commission to agent"
              quiet
              value={formatPercent(netCommission)}
            />
          )}
        </DetailSection>
      )}

      <DetailSection notIncluded={cashNotIncluded} title="Cash incentives">
        {cash.length > 0
          ? cash.map((item, index) => (
              <DetailTermRow
                key={`${item.type}-${index}`}
                label={itemLabel(item.type)}
                note={itemDescription(item.type)}
                value={
                  item.value == null ? "Included" : formatDollars(item.value)
                }
              />
            ))
          : null}
      </DetailSection>

      <DetailSection
        notIncluded={servicesNotIncluded}
        title="Service commitments"
      >
        {perks.length > 0
          ? perks.map((item, index) => (
              <DetailTermRow
                key={`${item.type}-${index}`}
                label={itemLabel(item.type)}
                value="Included"
              />
            ))
          : null}
      </DetailSection>

      {rebate != null ? (
        <p
          className="mt-6 border-t border-border-soft pt-4 text-xs leading-5 text-muted-foreground"
          data-testid="rebate-delivery-disclosure"
        >
          {cashTypes.has("closing_cost_credit") ? (
            <>
              Credits at closing (your rebate, and any closing cost credit)
              need lender approval and together can&apos;t exceed your actual
              closing costs and prepaids — excess may be reduced at
              underwriting.
            </>
          ) : (
            <>
              Your rebate is credited at closing. It needs lender approval
              and can&apos;t exceed your actual closing costs and prepaids —
              excess may be reduced at underwriting.
            </>
          )}
        </p>
      ) : (
        hasOffClosingOnly &&
        estimate && (
          <p
            className="mt-6 border-t border-border-soft pt-4 text-xs leading-5 text-muted-foreground"
            data-testid="rebate-delivery-disclosure"
          >
            Estimate. These credits are paid outside closing, so lender
            limits don&apos;t apply.
          </p>
        )
      )}
    </article>
  );
}

function OwnAgentBadge({ testId }: { testId: string }): ReactElement {
  return (
    <span
      className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-info/15 px-2.5 py-1 text-xs font-semibold text-info ring-1 ring-info/35"
      data-testid={testId}
    >
      From your agent
    </span>
  );
}
